import { Text } from '@televet/kibble-ui/Text';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageContainer } from '@/components/PageContainer';
import { TablePagination } from '@/components/TablePagination';
import { trpc } from '@/lib/trpc';
import { runStatusVariantMap } from '@/features/workflows/badgePalettes';
import { useListingFilters } from '@/features/listing-filters/useListingFilters';
import { useDebouncedFilterText } from '@/features/listing-filters/useDebouncedFilterText';
import {
  calendarDateToIsoEnd,
  calendarDateToIsoStart,
} from '@/features/listing-filters/calendarDate';
import { WorkflowRunFilters } from './-components/WorkflowRunFilters';
import {
  workflowRunsFiltersSchema,
  workflowRunsSearchSchema,
  type WorkflowRunsFilterValues,
} from './-lib/filters';

const RouteComponent = () => {
  const navigate = Route.useNavigate();
  const { page = 1, ...filters } = Route.useSearch();

  const [workflowNameDraft, setWorkflowNameDraft] = useDebouncedFilterText({
    value: filters.workflowName,
    onCommit: (next) =>
      navigate({
        replace: true,
        search: (prev) => ({ ...prev, workflowName: next, page: undefined }),
      }),
  });

  const { data: workflowsData } = useQuery(
    trpc.workflow.listWorkflows.queryOptions({ limit: 100 })
  );
  const { data: filterOptions } = useQuery(trpc.workflow.listRunFilterOptions.queryOptions());

  const applyFilters = useCallback(
    (next: WorkflowRunsFilterValues) => {
      // Any filter change invalidates the current page number.
      navigate({ search: { ...next, page: undefined } });
    },
    [navigate]
  );

  useListingFilters({
    page: 'workflow-runs',
    schema: workflowRunsFiltersSchema,
    filters,
    onRehydrate: applyFilters,
  });

  const where = useMemo(
    () => ({
      ...(filters.workflowId?.length && { workflowId: filters.workflowId }),
      ...(filters.workflowName && { workflowName: filters.workflowName }),
      ...(filters.workflowConfigurationId?.length && {
        workflowConfigurationId: filters.workflowConfigurationId,
      }),
      ...(filters.status?.length && { status: filters.status }),
      ...(filters.entityType && { entityType: filters.entityType }),
      ...(filters.triggeredBy && { triggeredBy: filters.triggeredBy }),
      createdFrom: calendarDateToIsoStart(filters.createdFrom),
      createdTo: calendarDateToIsoEnd(filters.createdTo),
    }),
    [filters]
  );

  const { data, isLoading } = useQuery(
    trpc.workflow.listRuns.queryOptions({ page, limit: 25, where })
  );

  const goToPage = (nextPage: number) => {
    // Omit page 1 from the URL to keep the default view clean.
    navigate({
      search: (prev) => ({ ...prev, page: nextPage > 1 ? nextPage : undefined }),
    });
  };

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Breadcrumbs items={[{ label: 'Runs' }]} />

        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Workflow Runs
          </Text>
        </Flex>

        <WorkflowRunFilters
          workflowNameDraft={workflowNameDraft}
          onWorkflowNameDraftChange={setWorkflowNameDraft}
          values={filters}
          onChange={applyFilters}
          workflows={workflowsData?.workflows ?? []}
          entityTypes={filterOptions?.entityTypes ?? []}
          triggeredBy={filterOptions?.triggeredBy ?? []}
        />
        <Table.Root size="sm" width="100%" variant="outline">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Run ID</Table.ColumnHeader>
              <Table.ColumnHeader>Workflow</Table.ColumnHeader>
              <Table.ColumnHeader>Config ID</Table.ColumnHeader>
              <Table.ColumnHeader>Config</Table.ColumnHeader>
              <Table.ColumnHeader>Status</Table.ColumnHeader>
              <Table.ColumnHeader>Current Step</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Intents</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Forms</Table.ColumnHeader>
              <Table.ColumnHeader>Created</Table.ColumnHeader>
              <Table.ColumnHeader>Updated</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data?.runs.map((run) => (
              <Table.Row
                key={run.id}
                cursor="pointer"
                _hover={{ bg: 'bg.muted' }}
                onClick={() =>
                  navigate({
                    to: '/workflow-runs/$runId',
                    params: { runId: run.id },
                  })
                }
              >
                <Table.Cell>
                  <Text fontSize="sm" fontFamily="mono">
                    {run.id.slice(0, 8)}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm">{run.workflow?.name ?? '-'}</Text>
                </Table.Cell>
                <Table.Cell maxW="160px">
                  {run.workflowConfiguration.ownerId ? (
                    <Text
                      fontSize="xs"
                      fontFamily="mono"
                      color="text.subtle"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      title={run.workflowConfiguration.ownerId}
                    >
                      {run.workflowConfiguration.ownerId.slice(0, 8)}
                    </Text>
                  ) : (
                    <Text fontSize="xs" color="text.subtle">
                      —
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Tag
                    label={run.workflowConfiguration.ownerType ?? 'Platform'}
                    variant="default"
                    size="sm"
                  />
                </Table.Cell>
                <Table.Cell>
                  <Tag
                    label={run.status}
                    variant={runStatusVariantMap[run.status] ?? 'default'}
                    size="sm"
                  />
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" variant="subtle">
                    {run.currentStep?.label ?? '-'}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="center">{run.intentCount}</Table.Cell>
                <Table.Cell textAlign="center">{run.formCount}</Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" variant="subtle">
                    {new Date(run.createdAt).toLocaleDateString()}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" variant="subtle">
                    {new Date(run.updatedAt).toLocaleString()}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        {!isLoading && data?.runs.length === 0 && (
          <Flex justify="center" align="center" p={8}>
            <Text variant="subtle">No workflow runs found</Text>
          </Flex>
        )}
        {data?.pagination && (
          <TablePagination
            page={data.pagination.page}
            totalPages={data.pagination.totalPages}
            hasPrev={data.pagination.hasPrev}
            hasNext={data.pagination.hasNext}
            summary={`${data.runs.length} runs on this page`}
            onPageChange={goToPage}
          />
        )}
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/workflow-runs/')({
  component: RouteComponent,
  validateSearch: workflowRunsSearchSchema,
});
